// Notation uses musical beats; playback continues to use the original seconds.
export function tempoClock(tempos = []) {
  const map = [{ time: 0, bpm: 120 }, ...tempos].sort((a, b) => a.time - b.time);
  let beat = 0;
  map.forEach((entry, i) => {
    if (i) beat += (entry.time - map[i - 1].time) * map[i - 1].bpm / 60;
    map[i] = { ...entry, beat };
  });
  const at = (value, field) => {
    let lo = 0, hi = map.length - 1;
    while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (map[mid][field] <= value) lo = mid; else hi = mid - 1; }
    return map[lo];
  };
  return {
    beat: time => { const t = at(time, 'time'); return t.beat + (time - t.time) * t.bpm / 60; },
    seconds: beat => { const t = at(beat, 'beat'); return t.time + (beat - t.beat) * 60 / t.bpm; },
  };
}

export function staffMeasures(score) {
  if (score.measures?.length) return score.measures;
  const clock = tempoClock(score.tempos), end = score.duration || score.notes.reduce((end, n) => Math.max(end, n.start + n.duration), 0);
  // Older MusicXML projects kept measure numbers on the notes only.
  const starts = [];
  for (const note of score.notes) {
    if (note.measure != null && note.measure !== starts.at(-1)?.number && note.start > (starts.at(-1)?.start ?? -1)) starts.push({ start: note.start, number: note.measure });
  }
  if (starts.length > 1) {
    starts[0].start = 0;
    return starts.map((m, i) => ({ ...m, duration: (starts[i + 1]?.start ?? end) - m.start }));
  }
  // Unmetered recordings and legacy MIDI use a four-quarter practice grid.
  const measures = [];
  for (let beat = 0; clock.seconds(beat) < end - .000001 && measures.length < 30000; beat += 4) {
    const start = clock.seconds(beat);
    measures.push({ start, duration: clock.seconds(beat + 4) - start, number: measures.length + 1, numerator: 4, denominator: 4 });
  }
  return measures;
}

export function noteStaff(note, layout) {
  if (layout?.staves.length === 1) return layout.staves[0];
  return note.staff ? (note.staff === 1 ? 1 : 2) : note.pitch >= 60 ? 1 : 2;
}

// Choose once for the whole song so rests and page changes never move a staff.
export function staffLayout(notes = []) {
  const sounding = notes.filter(note => !note.rejected);
  let staves = [...new Set(sounding.map(note => noteStaff(note)))].sort();
  if (staves.length === 2) {
    const low = sounding.filter(note => note.pitch < 60);
    const streams = new Set(sounding.map(note => `${note.track || 0}:${note.voice || '1'}`));
    // A single melody may dip as far as G3 (two ledger lines below treble).
    // Keep independent parts and substantial bass passages on two staves.
    if (low.length && low.length <= sounding.length / 4 && low.every(note => note.pitch >= 55) && streams.size === 1) {
      const ordered = sounding.slice().sort((a, b) => a.start - b.start);
      const monophonic = ordered.every((note, i) => !i || note.start >= ordered[i - 1].start + ordered[i - 1].duration - .000001);
      if (monophonic) staves = [1];
    }
  }
  return { staves, height: staves.length > 1 ? 226 : 142 };
}

const VALUES = [
  ['whole', 4, 0], ['half', 2, 0], ['quarter', 1, 0], ['eighth', .5, 1],
  ['16th', .25, 2], ['32nd', .125, 3], ['64th', .0625, 4], ['128th', .03125, 5],
];
export function noteRhythm(beats, written) {
  const choices = VALUES.flatMap(([type, base, flags]) => [0, 1, 2].map(dots => ({ type, base, flags, dots, beats: base * (2 - 1 / 2 ** dots), open: base >= 2, stem: base < 4 })));
  const original = written && choices.find(c => c.type === written.type && c.dots === (written.dots || 0));
  if (original && Math.abs(original.beats - beats) < .01) return original;
  if (original && Math.abs(original.beats * 2 / 3 - beats) < .001) return { ...original, beats, tuplet: 3 };
  return choices.reduce((best, c) => Math.abs(Math.log2(c.beats / beats)) < Math.abs(Math.log2(best.beats / beats)) ? c : best);
}

function rhythmicParts(beats, written) {
  const rhythm = noteRhythm(beats, written);
  // Unquantized performance durations use their nearest conventional value.
  if (Math.abs(rhythm.beats - beats) < .001 || Math.abs(beats * 32 - Math.round(beats * 32)) > .001) return [rhythm];
  const parts = [];
  let remaining = beats;
  while (remaining > .00001 && parts.length < 128) {
    const next = VALUES.flatMap(([, base]) => [base, base * 1.5, base * 1.75]).filter(value => value <= remaining + .00001).sort((a, b) => b - a)[0];
    if (!next) break;
    parts.push(noteRhythm(next)); remaining -= next;
  }
  return remaining < .00001 ? parts : [rhythm];
}

// Split sustained notes at barlines so each measure has its own tied notation.
export function staffSegments(notes, measures, clock) {
  const segments = [];
  let first = 0;
  for (const note of notes) {
    while (first < measures.length - 1 && measures[first].start + measures[first].duration <= note.start + .000001) first++;
    const end = note.start + note.duration;
    for (let i = first; i < measures.length && measures[i].start < end - .000001; i++) {
      const measure = measures[i], start = Math.max(note.start, measure.start), stop = Math.min(end, measure.start + measure.duration);
      if (stop <= start) continue;
      const startBeat = clock.beat(start), beats = clock.beat(stop) - startBeat, parts = rhythmicParts(beats, note);
      let offset = 0;
      parts.forEach((rhythm, index) => {
        const from = index ? clock.seconds(startBeat + offset) : start;
        offset += rhythm.beats;
        const to = index === parts.length - 1 ? stop : clock.seconds(startBeat + offset);
        segments.push({ note, start: from, end: to, measure: i, rhythm, tieIn: from > note.start + .000001, tieOut: to < end - .000001 });
      });
    }
  }
  return segments;
}

// Fill each staff/voice's silent intervals without mistaking overlapping chord
// tones or short performance releases for written rests.
export function staffRests(notes, measures, clock, layout = staffLayout(notes)) {
  const rests = [], staves = new Map(layout.staves.map(staff => [staff, new Map()]));
  for (const note of notes) {
    if (note.rejected) continue;
    const staff = noteStaff(note, layout);
    const voices = staves.get(staff), voice = note.voice || '1';
    if (!voices.has(voice)) voices.set(voice, []);
    const startBeat = clock.beat(note.start), beats = clock.beat(note.start + note.duration) - startBeat;
    const writtenBeats = rhythmicParts(beats, note).reduce((sum, rhythm) => sum + rhythm.beats, 0);
    voices.get(voice).push({ start: note.start, end: Math.max(note.start + note.duration, clock.seconds(startBeat + writtenBeats)) });
  }
  for (const [staff, voices] of staves) {
    if (!voices.size) voices.set('1', []);
    let voiceIndex = 0;
    for (const [voice, intervals] of voices) {
      const offset = (voiceIndex++ - (voices.size - 1) / 2) * 20;
      intervals.sort((a, b) => a.start - b.start);
      let first = 0;
      measures.forEach((measure, index) => {
        const end = measure.start + measure.duration, measureBeat = clock.beat(measure.start);
        const unit = 4 / (measure.denominator || 4);
        const beatSize = measure.numerator >= 6 && measure.numerator % 3 === 0 && measure.denominator >= 8 ? unit * 3 : unit;
        const add = (start, stop) => {
          let beat = clock.beat(start), endBeat = clock.beat(stop);
          if (endBeat - beat < .03125 - .000001) return;
          // Keep exact short rests, but omit tiny off-grid release gaps in MIDI
          // performances rather than inventing a cluster of 64th/128th rests.
          const beats = endBeat - beat;
          if (beats < .125 - .000001 && Math.abs(beats * 32 - Math.round(beats * 32)) > .001) return;
          const fullMeasure = Math.abs(start - measure.start) < .000001 && Math.abs(stop - end) < .000001;
          if (fullMeasure) {
            rests.push({ staff, voice, offset, start, end: stop, measure: index, rhythm: { ...noteRhythm(4), beats }, fullMeasure });
            return;
          }
          while (beat < endBeat - .000001) {
            // Finish a partial beat before writing longer rests on the beat.
            const position = (beat - measureBeat) / beatSize;
            const boundary = Math.abs(position - Math.round(position)) < .000001 ? endBeat : Math.min(endBeat, measureBeat + Math.ceil(position) * beatSize);
            const parts = rhythmicParts(boundary - beat);
            parts.forEach((rhythm, part) => {
              const from = clock.seconds(beat);
              beat = part === parts.length - 1 ? boundary : beat + rhythm.beats;
              rests.push({ staff, voice, offset, start: from, end: clock.seconds(beat), measure: index, rhythm });
            });
          }
        };
        while (first < intervals.length && intervals[first].end <= measure.start + .000001) first++;
        let cursor = measure.start;
        for (let i = first; i < intervals.length && intervals[i].start < end - .000001; i++) {
          const interval = intervals[i];
          if (interval.start > cursor + .000001) add(cursor, interval.start);
          cursor = Math.max(cursor, interval.end);
          if (cursor >= end - .000001) break;
        }
        if (cursor < end - .000001) add(cursor, end);
      });
    }
  }
  return rests.sort((a, b) => a.start - b.start || a.staff - b.staff);
}

// Beam within a beat (or a dotted beat in compound meter), never across a rest,
// voice, staff or barline. Simultaneous chord tones share the same beam.
export function beamGroups(segments, measures, clock, layout) {
  const streams = new Map(), groups = [];
  for (const segment of segments) {
    const n = segment.note, staff = noteStaff(n, layout);
    const key = `${n.track || 0}:${staff}:${n.voice || '1'}`;
    if (!streams.has(key)) streams.set(key, []);
    streams.get(key).push(segment);
  }
  for (const stream of streams.values()) {
    const events = [];
    for (const segment of stream.sort((a, b) => a.start - b.start)) {
      if (Math.abs((events.at(-1)?.[0].start ?? -1) - segment.start) < .000001) events.at(-1).push(segment);
      else events.push([segment]);
    }
    let group = [], previous, bucket;
    const flush = () => { if (group.length > 1) groups.push(group.flat()); group = []; };
    for (const event of events) {
      const first = event[0], measure = measures[first.measure], unit = 4 / (measure.denominator || 4);
      const beatSize = measure.numerator >= 6 && measure.numerator % 3 === 0 && measure.denominator >= 8 ? unit * 3 : unit;
      const nextBucket = `${first.measure}:${Math.floor((clock.beat(first.start) - clock.beat(measure.start) + .000001) / beatSize)}`;
      const eligible = event.every(s => s.rhythm.flags > 0 && Math.abs(s.end - first.end) < .000001 && s.rhythm.flags === first.rhythm.flags);
      if (!eligible || nextBucket !== bucket || previous && Math.abs(previous.end - first.start) > .001) flush();
      if (eligible) group.push(event);
      previous = first; bucket = nextBucket;
    }
    flush();
  }
  return groups;
}

export const MIN_NOTE_SPACING = 32;

export function staffPages(measures, segments, width, lookAhead = 6, minSpacing = MIN_NOTE_SPACING) {
  if (!measures.length) return [];
  const onsets = measures.map(() => new Map());
  for (const segment of segments) {
    const notes = onsets[segment.measure].get(segment.start) || [];
    notes.push(segment); onsets[segment.measure].set(segment.start, notes);
  }
  let cursor = 0;
  const extent = notes => ({
    left: notes?.some(s => s.note && (s.note.alter || s.note.accidental || [1,3,6,8,10].includes(s.note.pitch % 12))) ? 24 : 8,
    right: Math.max(8, ...((notes || []).map(s => Math.max(s.rhythm.flags ? 18 : 8, s.rhythm.dots ? 14 + (s.rhythm.dots - 1) * 5 : 8)))),
  });
  const metrics = measures.map((measure, index) => {
    const times = [...new Set([measure.start, ...onsets[index].keys(), measure.start + measure.duration])].sort((a, b) => a - b);
    const x = cursor, positions = [cursor + Math.max(10, extent(onsets[index].get(times[0])).left + 2)];
    for (let i = 1; i < times.length; i++) {
      const previous = extent(onsets[index].get(times[i - 1])), next = extent(onsets[index].get(times[i]));
      const end = i === times.length - 1, timed = 10 * Math.sqrt((times[i] - times[i - 1]) / measure.duration);
      // Never compress noteheads to make another measure fit. Bar gutters stay small.
      positions.push(positions.at(-1) + (end ? Math.max(minSpacing - 10, previous.right + 5) : Math.max(minSpacing, previous.right + next.left + 6) + timed));
    }
    cursor = positions.at(-1);
    return { ...measure, times, positions, x, width: cursor - x, index };
  });
  // Use the same measure width throughout the score, sized for its densest bar.
  // Keep that width on every page rather than stretching the last passage.
  const measureWidth = Math.max(...metrics.map(bar => bar.width));
  for (const bar of metrics) {
    const oldX = bar.x, scale = measureWidth / bar.width;
    bar.x = bar.index * measureWidth;
    bar.positions = bar.positions.map(x => bar.x + (x - oldX) * scale);
    bar.width = measureWidth;
  }
  const moments = metrics.flatMap(bar => bar.times.slice(0, -1).map((time, i) => ({ time, x: bar.positions[i], measure: bar.index, onset: onsets[bar.index].has(time), right: extent(onsets[bar.index].get(time)).right })));
  const endTime = measures.at(-1).start + measures.at(-1).duration, pages = [];
  let start = 0;
  while (start < moments.length) {
    const moment = moments[start], opening = start === 0, left = opening ? 74 : 12, right = width - 12;
    const origin = moment.time === metrics[moment.measure].start ? metrics[moment.measure].x : moment.x - 10;
    const limit = origin + right - left;
    let stop = start + 1;
    while (stop < moments.length && moments[stop].x + moments[stop].right <= limit) stop++;
    const final = stop === moments.length;
    const end = final ? endTime : moments[stop]?.time ?? endTime;
    const lastBar = metrics[moments[stop - 1].measure];
    const scale = 1;
    const bars = metrics.slice(moment.measure, lastBar.index + 1).map(bar => ({
      ...bar, x: (bar.x - origin) * scale + left, width: bar.width * scale, positions: bar.positions.map(x => (x - origin) * scale + left), complete: (bar.x + bar.width <= limit || final) && bar.start + bar.duration <= end,
    }));
    const endX = Math.min(right, bars.at(-1).x + bars.at(-1).width);
    // Center a complete score at its natural spacing, including its opening signs
    // and the thick final bar's outer edge. Final passages stop at that bar too.
    const offset = opening && final ? Math.max(0, (width - (endX + 6 - 12)) / 2 - 12) : 0;
    if (offset) for (const bar of bars) {
      bar.x += offset;
      bar.positions = bar.positions.map(x => x + offset);
    }
    pages.push({ first: moment.measure, last: bars.at(-1).index + 1, start: moment.time, end, left: left + offset, right: (final ? endX : right) + offset, staffLeft: 12 + offset, staffRight: (final ? endX + 4 : right) + offset, bars, opening, final });
    if (final || stop === moments.length) break;
    // Prefer the next bar; otherwise overlap a readable section of a dense bar.
    // Switching before the final visible onset leaves room to read ahead.
    let next = -1;
    for (let i = start + 1; i < stop - 1; i++) if (moments[i].time === metrics[moments[i].measure].start) next = i;
    if (next < 0) {
      next = start + 1;
      for (let i = start + 1; i < stop - 1; i++) if (moments[i].x - origin <= (right - left) * .65) next = i;
    }
    start = next;
  }
  return pages;
}

export function staffPageAt(pages, time) {
  let lo = 0, hi = pages.length - 1;
  while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (pages[mid].start <= time) lo = mid; else hi = mid - 1; }
  return pages[lo];
}

export function staffX(page, time) {
  const bar = page.bars.find(b => time < b.start + b.duration) || page.bars.at(-1);
  if (time <= bar.start) return bar.positions[0];
  let i = 0;
  while (i < bar.times.length - 2 && bar.times[i + 1] <= time) i++;
  const fraction = Math.max(0, Math.min(1, (time - bar.times[i]) / (bar.times[i + 1] - bar.times[i])));
  return Math.min(page.right, bar.positions[i] + fraction * (bar.positions[i + 1] - bar.positions[i]));
}
